import { useMemo, useState } from "react";
import { Archive, ChevronDown, ChevronUp, Pencil, Plus, RotateCcw } from "lucide-react";
import { useStore } from "../../lib/store";
import { showFeedback } from "../../lib/feedback";
import { Button } from "../../ui/Button";
import { IconButton } from "../../ui/IconButton";
import { Field, TextInput } from "../../ui/Field";
import { Select } from "../../ui/Select";
import { Switch } from "../../ui/Switch";
import { Dialog } from "../../ui/Dialog";
import { EmptyState } from "../../ui/EmptyState";
import type {
  LeadFieldDefinition,
  LeadFieldHeight,
  LeadFieldType,
  LeadFieldWidth,
} from "../../lib/types";

/**
 * Campos personalizados do formulário do funil.
 *
 * O Zênite mostra "Campos personalizados" como um cadastro editável ao lado de
 * tags e origens, mas aqui um campo não é uma palavra numa lista: ele tem
 * seção, tipo, largura, altura, obrigatoriedade e, nos seletores, opções
 * próprias. Esta tela é esse cadastro com o contrato real do domínio.
 *
 * Duas regras do §7.1 aparecem na interface, não só no comando: o tipo não muda
 * depois que existe valor gravado, e remover é arquivar — o histórico, a
 * exportação e o backup continuam precisando do campo antigo.
 */

const TIPOS: Array<{ value: LeadFieldType; label: string }> = [
  { value: "text", label: "Texto curto" },
  { value: "textarea", label: "Texto longo" },
  { value: "number", label: "Número" },
  { value: "currency", label: "Moeda" },
  { value: "url", label: "Endereço da web" },
  { value: "date", label: "Data" },
  { value: "boolean", label: "Sim ou não" },
  { value: "select", label: "Seleção única" },
  { value: "multi_select", label: "Seleção múltipla" },
];

const LARGURAS: Array<{ value: LeadFieldWidth; label: string }> = [
  { value: "half", label: "Meia linha" },
  { value: "full", label: "Linha inteira" },
];

const ALTURAS: Array<{ value: LeadFieldHeight; label: string }> = [
  { value: "compact", label: "Compacta" },
  { value: "normal", label: "Normal" },
  { value: "expanded", label: "Expandida" },
];

const tipoLabel = (type: LeadFieldType) => TIPOS.find((item) => item.value === type)?.label ?? type;

const temOpcoes = (type: LeadFieldType) => type === "select" || type === "multi_select";

/** Chave técnica do campo: estável, sem acento e sem colisão dentro do funil. */
function chaveDe(nome: string, usadas: Set<string>): string {
  const base =
    nome
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "") || "campo";
  if (!usadas.has(base)) return base;
  let n = 2;
  while (usadas.has(`${base}_${n}`)) n += 1;
  return `${base}_${n}`;
}

export function FormFieldsEditor() {
  const store = useStore();
  const funis = store.funnels.filter((funnel) => !funnel.archived);
  const [funilEscolhido, setFunilEscolhido] = useState("");
  const funilId = funis.some((funnel) => funnel.id === funilEscolhido)
    ? funilEscolhido
    : (funis[0]?.id ?? "");

  const secoes = useMemo(
    () =>
      store.formSections
        .filter((section) => section.funnelId === funilId && !section.archived)
        .sort((left, right) => left.order - right.order),
    [store.formSections, funilId],
  );
  const campos = useMemo(
    () => store.leadFields.filter((field) => field.funnelId === funilId),
    [store.leadFields, funilId],
  );
  const arquivados = campos.filter((field) => field.archived);

  const [novaSecao, setNovaSecao] = useState(false);
  const [renomeando, setRenomeando] = useState<{ id: string; name: string } | null>(null);
  const [editando, setEditando] = useState<{
    sectionId: string;
    field?: LeadFieldDefinition;
  } | null>(null);

  const moverSecao = async (index: number, delta: number) => {
    const alvo = index + delta;
    if (alvo < 0 || alvo >= secoes.length) return;
    const ids = secoes.map((section) => section.id);
    [ids[index], ids[alvo]] = [ids[alvo], ids[index]];
    if (!(await store.reorderFormSections(funilId, ids)))
      showFeedback("Não foi possível salvar a nova ordem das seções.");
  };

  const moverCampo = async (
    sectionId: string,
    lista: LeadFieldDefinition[],
    index: number,
    delta: number,
  ) => {
    const alvo = index + delta;
    if (alvo < 0 || alvo >= lista.length) return;
    const ids = lista.map((field) => field.id);
    [ids[index], ids[alvo]] = [ids[alvo], ids[index]];
    if (!(await store.reorderLeadFields(sectionId, ids)))
      showFeedback("Não foi possível salvar a nova ordem dos campos.");
  };

  if (!funilId)
    return (
      <EmptyState
        title="Nenhum funil ativo"
        description="Os campos pertencem ao formulário de um funil. Crie um funil para cadastrá-los."
      />
    );

  return (
    <section className="cs-formfields">
      <header>
        <h2>Campos personalizados</h2>
        <p>
          Informações próprias do seu formulário, por funil. Elas aparecem na criação, nas duas
          edições e na visualização do cadastro, na ordem definida aqui.
        </p>
      </header>

      {funis.length > 1 && (
        <Field label="Funil" className="cs-ff-funnel">
          {({ id }) => (
            <Select
              id={id}
              size="field"
              label="Funil dos campos"
              value={funilId}
              options={funis.map((funnel) => ({ value: funnel.id, label: funnel.name }))}
              onChange={setFunilEscolhido}
            />
          )}
        </Field>
      )}

      {secoes.length === 0 && (
        <EmptyState
          title="Nenhuma seção ainda"
          description="Os campos moram em seções do formulário. Crie a primeira para começar."
        />
      )}

      {secoes.map((section, index) => {
        const doSetor = campos
          .filter((field) => field.sectionId === section.id && !field.archived)
          .sort((left, right) => left.order - right.order);
        return (
          <article key={section.id} className="cs-ff-section">
            <header>
              <h3>{section.name}</h3>
              <span className="cs-ff-count">
                {doSetor.length} {doSetor.length === 1 ? "campo" : "campos"}
              </span>
              <span className="cs-ff-spacer" />
              <IconButton
                size="compact"
                variant="bare"
                label={`Renomear seção ${section.name}`}
                icon={<Pencil size={14} aria-hidden="true" />}
                onClick={() => setRenomeando({ id: section.id, name: section.name })}
              />
              <IconButton
                size="compact"
                variant="bare"
                label={`Subir seção ${section.name}`}
                disabled={index === 0}
                icon={<ChevronUp size={14} aria-hidden="true" />}
                onClick={() => void moverSecao(index, -1)}
              />
              <IconButton
                size="compact"
                variant="bare"
                label={`Descer seção ${section.name}`}
                disabled={index === secoes.length - 1}
                icon={<ChevronDown size={14} aria-hidden="true" />}
                onClick={() => void moverSecao(index, 1)}
              />
            </header>
            {doSetor.length === 0 ? (
              <p className="cs-field-note">Seção sem campos.</p>
            ) : (
              <ul className="cs-ff-list">
                {doSetor.map((field, fieldIndex) => (
                  <li key={field.id}>
                    <span className="cs-ff-name">{field.name}</span>
                    <span className="cs-ff-tag">{tipoLabel(field.type)}</span>
                    {field.required && <span className="cs-ff-tag is-strong">Obrigatório</span>}
                    {field.width === "full" && <span className="cs-ff-tag">Linha inteira</span>}
                    {temOpcoes(field.type) && (
                      <span className="cs-ff-tag">
                        {
                          store.leadFieldOptions.filter(
                            (option) => option.fieldId === field.id && !option.archived,
                          ).length
                        }{" "}
                        opções
                      </span>
                    )}
                    <span className="cs-ff-spacer" />
                    <IconButton
                      size="compact"
                      variant="bare"
                      label={`Editar campo ${field.name}`}
                      icon={<Pencil size={14} aria-hidden="true" />}
                      onClick={() => setEditando({ sectionId: section.id, field })}
                    />
                    <IconButton
                      size="compact"
                      variant="bare"
                      label={`Subir campo ${field.name}`}
                      disabled={fieldIndex === 0}
                      icon={<ChevronUp size={14} aria-hidden="true" />}
                      onClick={() => void moverCampo(section.id, doSetor, fieldIndex, -1)}
                    />
                    <IconButton
                      size="compact"
                      variant="bare"
                      label={`Descer campo ${field.name}`}
                      disabled={fieldIndex === doSetor.length - 1}
                      icon={<ChevronDown size={14} aria-hidden="true" />}
                      onClick={() => void moverCampo(section.id, doSetor, fieldIndex, 1)}
                    />
                    <IconButton
                      size="compact"
                      variant="bare"
                      label={`Arquivar campo ${field.name}`}
                      icon={<Archive size={14} aria-hidden="true" />}
                      onClick={async () => {
                        if (await store.archiveLeadField(field.id, true))
                          showFeedback(`“${field.name}” foi arquivado.`, "success");
                        else showFeedback("Não foi possível arquivar o campo.");
                      }}
                    />
                  </li>
                ))}
              </ul>
            )}
            <Button
              size="compact"
              variant="text"
              icon={<Plus size={14} aria-hidden="true" />}
              onClick={() => setEditando({ sectionId: section.id })}
            >
              Novo campo nesta seção
            </Button>
          </article>
        );
      })}

      <div className="cs-ff-foot">
        <Button
          variant="secondary"
          icon={<Plus size={15} aria-hidden="true" />}
          onClick={() => setNovaSecao(true)}
        >
          Nova seção
        </Button>
      </div>

      {arquivados.length > 0 && (
        <article className="cs-ff-section is-archived">
          <header>
            <h3>Arquivados</h3>
            <span className="cs-ff-count">{arquivados.length}</span>
          </header>
          <p className="cs-field-note">
            Fora do formulário e dos filtros, preservados no histórico, na exportação e no backup.
          </p>
          <ul className="cs-ff-list">
            {arquivados.map((field) => (
              <li key={field.id}>
                <span className="cs-ff-name">{field.name}</span>
                <span className="cs-ff-tag">{tipoLabel(field.type)}</span>
                <span className="cs-ff-spacer" />
                <IconButton
                  size="compact"
                  variant="bare"
                  label={`Restaurar campo ${field.name}`}
                  icon={<RotateCcw size={14} aria-hidden="true" />}
                  onClick={async () => {
                    if (await store.archiveLeadField(field.id, false))
                      showFeedback(`“${field.name}” voltou ao formulário.`, "success");
                    else showFeedback("Não foi possível restaurar o campo.");
                  }}
                />
              </li>
            ))}
          </ul>
        </article>
      )}

      {novaSecao && (
        <SectionDialog
          onClose={() => setNovaSecao(false)}
          onSubmit={(name) => store.addFormSection(funilId, name)}
        />
      )}
      {renomeando && (
        <SectionDialog
          name={renomeando.name}
          onClose={() => setRenomeando(null)}
          onSubmit={(name) => store.updateFormSection(renomeando.id, { name })}
        />
      )}
      {editando && (
        <FieldDialog
          funnelId={funilId}
          sectionId={editando.sectionId}
          field={editando.field}
          onClose={() => setEditando(null)}
        />
      )}
    </section>
  );
}

function SectionDialog({
  name: inicial = "",
  onClose,
  onSubmit,
}: {
  name?: string;
  onClose: () => void;
  onSubmit: (name: string) => Promise<boolean>;
}) {
  const [name, setName] = useState(inicial);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (saving) return;
    if (!name.trim()) {
      setError("Dê um nome à seção.");
      return;
    }
    setSaving(true);
    const ok = await onSubmit(name.trim());
    setSaving(false);
    if (!ok) {
      setError("Não foi possível salvar a seção.");
      return;
    }
    showFeedback(inicial ? "Seção renomeada." : "Seção criada.", "success");
    onClose();
  };

  return (
    <Dialog
      open
      onClose={onClose}
      dirty={name.trim() !== inicial.trim()}
      title={inicial ? "Renomear seção" : "Nova seção"}
      description="As seções agrupam os campos do formulário do funil."
      footer={
        <>
          <Button variant="text" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <span className="cs-ff-spacer" />
          <Button onClick={submit} loading={saving}>
            {inicial ? "Salvar" : "Criar seção"}
          </Button>
        </>
      }
    >
      <Field label="Nome" required error={error}>
        {({ id, describedBy, invalid }) => (
          <TextInput
            id={id}
            value={name}
            maxLength={60}
            placeholder="Ex.: Diagnóstico"
            aria-describedby={describedBy}
            aria-invalid={invalid}
            onChange={(event) => setName(event.target.value)}
          />
        )}
      </Field>
    </Dialog>
  );
}

function FieldDialog({
  funnelId,
  sectionId,
  field,
  onClose,
}: {
  funnelId: string;
  sectionId: string;
  field?: LeadFieldDefinition;
  onClose: () => void;
}) {
  const store = useStore();
  const [name, setName] = useState(field?.name ?? "");
  const [type, setType] = useState<LeadFieldType>(field?.type ?? "text");
  const [width, setWidth] = useState<LeadFieldWidth>(field?.width ?? "half");
  const [height, setHeight] = useState<LeadFieldHeight>(field?.height ?? "normal");
  const [required, setRequired] = useState(field?.required ?? false);
  const [inMetrics, setInMetrics] = useState(field?.inMetrics ?? false);
  const [placeholder, setPlaceholder] = useState(field?.placeholder ?? "");
  const [novaOpcao, setNovaOpcao] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  // Tipo com valor gravado não muda: trocá-lo reinterpretaria o histórico
  // (§7.1). O caminho é criar um campo substituto e arquivar este.
  const temValor = Boolean(
    field && store.leadFieldValues.some((value) => value.fieldId === field.id),
  );
  const opcoes = field
    ? store.leadFieldOptions
        .filter((option) => option.fieldId === field.id && !option.archived)
        .sort((left, right) => left.order - right.order)
    : [];

  const submit = async () => {
    if (saving) return;
    if (!name.trim()) {
      setError("Dê um nome ao campo.");
      return;
    }
    setSaving(true);
    let ok: boolean;
    if (field) {
      ok = await store.updateLeadField(field.id, {
        name: name.trim(),
        type,
        width,
        height,
        required,
        inMetrics,
        placeholder: placeholder.trim() || undefined,
      });
    } else {
      const usadas = new Set(
        store.leadFields.filter((item) => item.funnelId === funnelId).map((item) => item.key),
      );
      ok = await store.addLeadField({
        funnelId,
        sectionId,
        name: name.trim(),
        key: chaveDe(name, usadas),
        type,
        width,
        height,
        required,
        inForm: true,
        inMetrics,
        placeholder: placeholder.trim() || undefined,
      });
    }
    setSaving(false);
    if (!ok) {
      setError("Não foi possível salvar o campo.");
      return;
    }
    showFeedback(field ? "Campo atualizado." : "Campo criado.", "success");
    onClose();
  };

  const adicionaOpcao = async () => {
    if (!field || !novaOpcao.trim()) return;
    const id = await store.addLeadFieldOption(field.id, novaOpcao.trim());
    if (!id) {
      showFeedback("Não foi possível criar a opção.");
      return;
    }
    setNovaOpcao("");
  };

  return (
    <Dialog
      open
      onClose={onClose}
      dirty={name.trim() !== (field?.name ?? "")}
      title={field ? "Editar campo" : "Novo campo"}
      description="Como o campo aparece no formulário do funil."
      footer={
        <>
          <Button variant="text" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <span className="cs-ff-spacer" />
          <Button onClick={submit} loading={saving}>
            {field ? "Salvar" : "Criar campo"}
          </Button>
        </>
      }
    >
      <Field label="Nome" required error={error}>
        {({ id, describedBy, invalid }) => (
          <TextInput
            id={id}
            value={name}
            maxLength={60}
            placeholder="Ex.: Investimento atual em mídia"
            aria-describedby={describedBy}
            aria-invalid={invalid}
            onChange={(event) => setName(event.target.value)}
          />
        )}
      </Field>
      <div className="cs-ff-grid">
        <Field
          label="Tipo"
          help={temValor ? "O campo já tem respostas gravadas: o tipo não muda." : undefined}
        >
          {({ id, describedBy }) => (
            <Select
              id={id}
              size="field"
              label="Tipo do campo"
              value={type}
              options={TIPOS}
              disabled={temValor}
              disabledReason="Crie um campo substituto e arquive este para preservar o histórico."
              describedBy={describedBy}
              onChange={setType}
            />
          )}
        </Field>
        <Field label="Largura">
          {({ id }) => (
            <Select
              id={id}
              size="field"
              label="Largura do campo"
              value={width}
              options={LARGURAS}
              onChange={setWidth}
            />
          )}
        </Field>
        <Field label="Altura">
          {({ id }) => (
            <Select
              id={id}
              size="field"
              label="Altura do campo"
              value={height}
              options={ALTURAS}
              onChange={setHeight}
            />
          )}
        </Field>
        <Field label="Dica dentro do campo">
          {({ id }) => (
            <TextInput
              id={id}
              value={placeholder}
              maxLength={80}
              placeholder="Opcional"
              onChange={(event) => setPlaceholder(event.target.value)}
            />
          )}
        </Field>
      </div>
      <div className="cs-ff-switches">
        <Switch checked={required} onChange={setRequired} label="Obrigatório no formulário" />
        <Switch checked={inMetrics} onChange={setInMetrics} label="Mensurável na análise" />
      </div>
      {temOpcoes(type) && (
        <section className="cs-ff-options">
          <h4>Opções</h4>
          {!field ? (
            <p className="cs-field-note">Salve o campo para cadastrar as opções dele.</p>
          ) : (
            <>
              {opcoes.length === 0 ? (
                <p className="cs-field-note">Nenhuma opção ainda.</p>
              ) : (
                <ul>
                  {opcoes.map((option) => (
                    <li key={option.id}>{option.name}</li>
                  ))}
                </ul>
              )}
              <div className="cs-ff-option-add">
                <TextInput
                  value={novaOpcao}
                  maxLength={60}
                  aria-label="Nova opção"
                  placeholder="Nova opção"
                  onChange={(event) => setNovaOpcao(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      void adicionaOpcao();
                    }
                  }}
                />
                <Button size="compact" variant="secondary" onClick={adicionaOpcao}>
                  Adicionar
                </Button>
              </div>
              <p className="cs-field-note">
                Renomear e remover uma opção ainda não existe como comando: o valor gravado nos
                cadastros aponta para ela, e trocá-lo aqui reescreveria respostas antigas.
              </p>
            </>
          )}
        </section>
      )}
    </Dialog>
  );
}
